/**
 * @file CorsDiagnosticTool.js
 * @description Herramienta para diagnóstico y monitoreo de problemas CORS
 * @version 1.0.0
 * @author Nubelytics Engineering Team
 */

import corsProxyRegistry from './CorsProxyRegistry';
import axios from 'axios';

/**
 * Clase para realizar diagnósticos y verificaciones de configuración CORS
 */
class CorsDiagnosticTool {
  constructor() {
    this.endpoints = [
      {
        name: 'API Principal',
        url: 'https://api.nubelytics.com/api/health',
        expectHeaders: ['access-control-allow-origin', 'access-control-allow-headers']
      },
      {
        name: 'Sincronización',
        url: 'https://sync.nubelytics.com/api/health',
        expectHeaders: ['access-control-allow-origin', 'access-control-allow-headers']
      },
      {
        name: 'Firebase Functions V2',
        url: 'https://us-central1-nubelytics.cloudfunctions.net/healthcheckV1',
        expectHeaders: ['access-control-allow-origin', 'access-control-allow-headers']
      }
    ];
    
    this.results = [];
    this.inProgress = false;
  }
  
  /**
   * Añade un endpoint personalizado para diagnóstico
   * @param {string} name - Nombre descriptivo del endpoint
   * @param {string} url - URL completa a verificar
   * @param {Array<string>} [expectHeaders] - Cabeceras esperadas
   */
  addEndpoint(name, url, expectHeaders = ['access-control-allow-origin']) {
    if (!name || !url) return;
    
    this.endpoints.push({
      name,
      url,
      expectHeaders
    });
  }
  
  /**
   * Verifica si las cabeceras CORS cumplen con los requisitos especificados
   * @param {Object} headers - Cabeceras de respuesta
   * @param {Array<string>} expectHeaders - Lista de cabeceras esperadas
   * @returns {Object} Resultados de la verificación
   */
  verifyHeaders(headers, expectHeaders) {
    const results = {
      success: true,
      missing: [],
      issues: [],
      details: {}
    };
    
    // Convertir todas las claves a minúsculas para comparación case-insensitive
    const normalizedHeaders = {};
    Object.keys(headers).forEach(key => {
      normalizedHeaders[key.toLowerCase()] = headers[key];
    });
    
    // Verificar cabeceras requeridas
    for (const header of expectHeaders) {
      const headerLower = header.toLowerCase();
      if (!normalizedHeaders[headerLower]) {
        results.success = false;
        results.missing.push(header);
        continue;
      }
      
      // Validaciones específicas para cada cabecera
      if (headerLower === 'access-control-allow-origin') {
        const value = normalizedHeaders[headerLower];
        results.details[header] = value;
        
        // Comprobar si es un valor específico (no wildcard)
        if (value === '*') {
          results.issues.push('Access-Control-Allow-Origin usa wildcard (*), lo que no es recomendado para APIs autenticadas');
        }
      } else if (headerLower === 'access-control-allow-headers') {
        const value = normalizedHeaders[headerLower];
        results.details[header] = value;
        
        // Verificar si incluye x-monitor-check
        if (!value.toLowerCase().includes('x-monitor-check')) {
          results.issues.push('Access-Control-Allow-Headers no incluye x-monitor-check');
        }
      } else {
        results.details[header] = normalizedHeaders[headerLower];
      }
    }
    
    return results;
  }
  
  /**
   * Realiza una petición OPTIONS para verificar el preflight CORS
   * @param {string} url - URL a verificar
   * @returns {Promise<Object>} Resultado de la verificación
   */
  async checkPreflightOptions(url) {
    try {
      const response = await axios({
        method: 'OPTIONS',
        url,

        timeout: 5000
      });
      
      return {
        success: true,
        status: response.status,
        headers: response.headers
      };
    } catch (error) {
      // Si el error tiene respuesta, puede ser un 204 válido
      if (error.response) {
        return {
          success: error.response.status === 204,
          status: error.response.status,
          headers: error.response.headers
        };
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  /**
   * Realiza un diagnóstico completo de todos los endpoints
   * @returns {Promise<Array>} Resultados del diagnóstico
   */
  async runFullDiagnosis() {
    if (this.inProgress) {
      return { error: 'Ya hay un diagnóstico en progreso' };
    }
    
    this.inProgress = true;
    this.results = [];
    
    // Crear cliente Axios específico para diagnóstico
    const client = axios.create();
    
    try {
      // Primero prueba sin proxy
      for (const endpoint of this.endpoints) {
        try {
          console.info(`[CorsDiagnosticTool] Verificando endpoint: ${endpoint.name}`);
          
          const endpointResult = {
            name: endpoint.name,
            url: endpoint.url,
            direct: { success: false },
            withProxy: { success: false },
            preflight: { success: false }
          };
          
          // 1. Prueba directa
          try {
            const directResponse = await client.get(endpoint.url, {
              headers: { 'x-monitor-check': 'true' },
              timeout: 5000
            });
            
            endpointResult.direct = {
              success: true,
              status: directResponse.status,
              headers: directResponse.headers,
              headerCheck: this.verifyHeaders(directResponse.headers, endpoint.expectHeaders)
            };
          } catch (directError) {
            endpointResult.direct = {
              success: false,
              error: directError.message,
              status: directError.response?.status,
              headers: directError.response?.headers
            };
            
            // Si hay cabeceras en el error, verificarlas
            if (directError.response?.headers) {
              endpointResult.direct.headerCheck = this.verifyHeaders(
                directError.response.headers,
                endpoint.expectHeaders
              );
            }
          }
          
          // 2. Prueba de preflight OPTIONS
          endpointResult.preflight = await this.checkPreflightOptions(endpoint.url);
          
          // 3. Prueba con proxy
          const proxyTest = await corsProxyRegistry.testCorsConnection(endpoint.url);
          endpointResult.withProxy = proxyTest;
          
          this.results.push(endpointResult);
        } catch (endpointError) {
          this.results.push({
            name: endpoint.name,
            url: endpoint.url,
            error: endpointError.message,
            success: false
          });
        }
      }
      
      this.inProgress = false;
      return this.results;
    } catch (error) {
      this.inProgress = false;
      return {
        error: error.message,
        success: false
      };
    }
  }
  
  /**
   * Exporta los resultados de diagnóstico como texto para compartir
   * @returns {string} Resumen del diagnóstico en formato texto
   */
  exportResultsSummary() {
    if (!this.results || this.results.length === 0) {
      return 'No hay resultados de diagnóstico disponibles';
    }
    
    let output = '=== DIAGNÓSTICO CORS NUBELYTICS ===\n';
    output += `Fecha: ${new Date().toISOString()}\n\n`;
    
    for (const result of this.results) {
      output += `=== ${result.name} (${result.url}) ===\n`;
      
      if (result.error) {
        output += `❌ ERROR: ${result.error}\n`;
        continue;
      }
      
      // Resumen petición directa
      output += '1. Petición directa: ';
      if (result.direct.success) {
        output += '✅ OK\n';
        output += `  - Status: ${result.direct.status}\n`;
        
        // Análisis de cabeceras
        if (result.direct.headerCheck) {
          const check = result.direct.headerCheck;
          if (check.success) {
            output += '  - Cabeceras CORS: ✅ OK\n';
          } else {
            output += '  - Cabeceras CORS: ❌ Problemas detectados\n';
            if (check.missing.length > 0) {
              output += `    - Faltan: ${check.missing.join(', ')}\n`;
            }
            if (check.issues.length > 0) {
              output += `    - Problemas: ${check.issues.join('; ')}\n`;
            }
          }
          
          // Mostrar valores de cabeceras críticas
          if (check.details['access-control-allow-origin']) {
            output += `  - Origin: ${check.details['access-control-allow-origin']}\n`;
          }
        }
      } else {
        output += `❌ Falló (${result.direct.status || '?'}) - ${result.direct.error || 'Error desconocido'}\n`;
      }
      
      // Resumen preflight OPTIONS
      output += '2. Preflight OPTIONS: ';
      if (result.preflight.success) {
        output += `✅ OK (${result.preflight.status})\n`;
      } else {
        output += `❌ Falló - ${result.preflight.error || 'Error desconocido'}\n`;
      }
      
      // Resumen proxy
      output += '3. Con Proxy CORS: ';
      if (result.withProxy.success) {
        output += '✅ OK\n';
      } else {
        output += `❌ Falló - ${result.withProxy.error || 'Error desconocido'}\n`;
      }
      
      output += '\n';
    }
    
    output += '=== DOMINIOS CON PROXY FORZADO ===\n';
    output += corsProxyRegistry.getProxyDomains().join('\n');
    
    return output;
  }
}

// Exportar instancia única
const corsDiagnosticTool = new CorsDiagnosticTool();
export default corsDiagnosticTool;
